/**
 * LiveSessions — /live hub. Lists upcoming & past instructor-led live
 * sessions and lets hosts (admins + instructors) schedule new ones.
 *
 * Sessions run on Jitsi Meet embedded — no API keys or accounts required.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import AdSlot from '../components/AdSlot';
import { Button } from '../components/ui/button';
import {
  Video, Calendar, Clock, Users, Plus, ChevronRight, CircleDot, X,
  Sparkles, ShieldCheck,
} from 'lucide-react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';

const canHost = (user) => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  const tier = (user.tier || '').toLowerCase();
  return ['admin', 'owner', 'super_admin'].includes(role)
      || ['business', 'enterprise', 'instructor'].includes(tier);
};

const statusPill = {
  live:      { label: 'LIVE',      cls: 'bg-rose-500 text-white',            icon: <CircleDot className="w-3 h-3 animate-pulse" /> },
  scheduled: { label: 'Scheduled', cls: 'bg-primary/15 text-primary',        icon: <Calendar className="w-3 h-3" /> },
  ended:     { label: 'Ended',     cls: 'bg-muted text-muted-foreground',    icon: <Clock className="w-3 h-3" /> },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-500/20 text-slate-600',    icon: <X className="w-3 h-3" /> },
};

const SessionCard = ({ session, onOpen }) => {
  const pill = statusPill[session.status] || statusPill.scheduled;
  let when = '';
  try {
    const start = parseISO(session.scheduled_start);
    when = `${format(start, 'EEE MMM d · h:mm a')} · ${formatDistanceToNow(start, { addSuffix: true })}`;
  } catch (_) { /* noop */ }

  return (
    <article
      className="pro-card p-4 hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onOpen}
      data-testid={`live-session-card-${session.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Video className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${pill.cls}`}>
              {pill.icon} {pill.label}
            </span>
            {session.is_host && (
              <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-emerald-500/15 text-emerald-700">
                <ShieldCheck className="w-3 h-3" /> You host
              </span>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto">{when}</span>
          </div>
          <h3 className="font-heading text-base font-extrabold text-foreground mt-1 line-clamp-1 group-hover:text-primary transition-colors">
            {session.title}
          </h3>
          {session.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{session.description}</p>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {session.live_attendee_count ?? 0} live</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {session.duration_minutes} min</span>
            {session.host_name && <span>· hosted by <b className="text-foreground">{session.host_name}</b></span>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
      </div>
    </article>
  );
};

// --- Scheduling modal (admins/instructors) ---
const ScheduleModal = ({ open, onClose, onCreated, API }) => {
  const now = new Date();
  const default_start = new Date(now.getTime() + 30 * 60 * 1000); // 30 min from now
  const [form, setForm] = useState({
    title: '',
    description: '',
    scheduled_start: default_start.toISOString().slice(0, 16), // for datetime-local
    duration_minutes: 60,
    visibility: 'public',
  });
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!form.title.trim()) { toast.info('Give it a title'); return; }
    try {
      setSaving(true);
      const iso = new Date(form.scheduled_start).toISOString();
      const { data } = await axios.post(`${API}/live-sessions`, {
        title: form.title.trim(),
        description: form.description.trim(),
        scheduled_start: iso,
        duration_minutes: Number(form.duration_minutes) || 60,
        visibility: form.visibility,
      });
      toast.success('Session scheduled');
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4" data-testid="schedule-session-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
            <Video className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Schedule a live session</div>
            <h3 className="font-heading text-base font-extrabold text-foreground">New meeting</h3>
          </div>
          <button type="button" onClick={onClose} className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <Field label="Title" required>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Advanced Rehydration Workshop"
              data-testid="session-title-input"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
            />
          </Field>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="What will you cover? Include a rough agenda for attendees."
              data-testid="session-description-input"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start" required>
              <input
                type="datetime-local"
                value={form.scheduled_start}
                onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })}
                data-testid="session-start-input"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
              />
            </Field>
            <Field label="Duration (min)">
              <input
                type="number"
                min={10}
                max={480}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                data-testid="session-duration-input"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
              />
            </Field>
          </div>
          <Field label="Visibility">
            <select
              value={form.visibility}
              onChange={(e) => setForm({ ...form, visibility: e.target.value })}
              data-testid="session-visibility-input"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
            >
              <option value="public">Public — anyone with an account</option>
              <option value="link_only">Link-only — anyone with the link</option>
              <option value="invite_only">Invite only</option>
            </select>
          </Field>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving} className="rounded-full font-bold" data-testid="session-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="session-create-btn">
            {saving ? 'Scheduling…' : 'Schedule session'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

// -------------------------------------------------------------------------

const LiveSessions = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [scope, setScope] = useState('upcoming');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);

  const isHost = useMemo(() => canHost(user), [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/live-sessions?scope=${scope}`);
      setSessions(data.sessions || []);
    } catch (_) { toast.error('Could not load sessions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope, API]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="eyebrow text-primary">Live Sessions</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight">
              Learn together, in real time
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Weekly office hours, guest workshops, and Q&amp;A hosted by instructors.
              Cameras, whiteboards, breakout rooms, shared notes &mdash; all in your browser.
            </p>
          </div>
          {isHost && (
            <Button
              onClick={() => setScheduling(true)}
              data-testid="schedule-session-btn"
              className="ml-auto rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-1" /> Schedule
            </Button>
          )}
        </div>

        {/* Scope tabs */}
        <div className="mb-5 inline-flex rounded-full border border-border overflow-hidden text-xs font-bold" data-testid="live-scope-tabs">
          {[
            { k: 'upcoming', l: 'Upcoming' },
            { k: 'past',     l: 'Past'     },
            { k: 'mine',     l: 'Mine'     },
          ].map((s) => (
            <button
              key={s.k}
              type="button"
              onClick={() => setScope(s.k)}
              data-testid={`live-scope-${s.k}`}
              className={`px-3.5 h-8 uppercase tracking-widest transition-colors ${
                scope === s.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>

        {/* List */}
        {loading && (
          <div className="text-center py-16 text-muted-foreground text-sm">Loading sessions…</div>
        )}
        {!loading && sessions.length === 0 && (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl">
            <Video className="w-8 h-8 mx-auto text-muted-foreground opacity-50 mb-3" />
            <div className="font-heading text-lg font-extrabold text-foreground">No sessions {scope === 'upcoming' ? 'coming up' : 'yet'}</div>
            <p className="text-sm text-muted-foreground mt-1">
              {isHost
                ? 'Schedule the first one to kick things off.'
                : 'Check back soon — instructors host weekly sessions.'}
            </p>
            {isHost && (
              <Button
                onClick={() => setScheduling(true)}
                className="mt-4 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="empty-schedule-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Schedule a session
              </Button>
            )}
          </div>
        )}
        {!loading && sessions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="live-session-list">
            {sessions.map((s) => (
              <SessionCard key={s.id} session={s} onOpen={() => navigate(`/live/${s.id}`)} />
            ))}
          </div>
        )}

        {/* Sponsor slot below the list */}
        <div className="mt-6">
          <AdSlot location="live_hub_side" variant="banner" />
        </div>
      </div>

      <ScheduleModal
        open={scheduling}
        onClose={() => setScheduling(false)}
        onCreated={(s) => { setScheduling(false); navigate(`/live/${s.id}`); }}
        API={API}
      />
    </div>
  );
};

export default LiveSessions;
